import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, json } from "react-router-dom";
import Swal from "sweetalert2";
import useAuth from "../../hooks/useAuth";
import UseAxiosPrivate from "../../hooks/useAxiosPrivate";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const CampaignName = (props) => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const axiosprivate = UseAxiosPrivate();
  const user_id = auth?.user_details?.id;

  const [templates, setTemplates] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [showNameExist,setShowNameExist] = useState(false);

  // ── Fetch campaign templates ──────────────────────────────────────────────
  useEffect(() => {
    if (!user_id) return;
    let cancelled = false;

    const fetchTemplates = async () => {
      try {
        const { data } = await axiosprivate.get(
          `${API}/contactcentre/campaign_template/${user_id}/`
        );
        if (!cancelled) setTemplates(data);
      } catch (err) {
        if (!cancelled) console.error("Failed to fetch templates:", err);
      }
    };

    fetchTemplates();
    return () => { cancelled = true; };
  }, [user_id]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const val = e.target.value;
    props.setData((old) => ({ ...old, campaign_name: val.trimStart() }));
  };

  const [selectedTemplate, setSelectedTemplate] = useState()

  const handleTemplate = useCallback(async (e) => {
    const camp_id = e.target.value;
    setSelectedTemplate(camp_id)

    if (camp_id === "0") {
      props.setData({
        ...props.data,
        campaignId: "",
        // campaign_name: "",
        agents: [],
        dids: [],
        caller_id_mode: null,
        schedule_time: [],
        call_type: "",
        questions: [],
        file: "",
        invalidFile: "",
        labels: [],
        customerNumberMask: false,
        did: "",
        campaignStartTime: "",
        nextCallTime: "",
        maxRetry: "",
        progressiveDialer: false,
        predictiveDialer: false,
        customer_ratio: "",
        voice_boat_template: "",
        voice_bot_call_limit: 1,
        retry_type: "",
        retry_interval_value: "",
        retry_time_gap: "Fixed",
        retry_schedule_data: [],
      })
      return
    }

    if (!camp_id) return;

    setLoadingTemplate(true);
    try {
      const { data } = await axios.post(
        `${API}/contactcentre/get_campaign_data/`,
        { user_id: auth?.user_details?.user, campaignName: camp_id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth?.accessToken}`,
          },
        }
      );

      // alert(JSON.stringify(data.campaignName))
      // alert(JSON.stringify(props.data.campaign_name))
      
      props.setData({
        ...props.data,
        agents: data.agents,
        schedule_time: data.scheduleTime,
        call_type: data.callType,
        questions: data.questions,
        labels: (data.labels || []).map(label => ({
          name: label.name,
          options: Array.isArray(label.options)
            ? label.options
            : (label.options || "")
                .split(",")
                .map(opt => opt.trim())
                .filter(Boolean)
        })),
        customerNumberMask: data.customerNumPermission,
        did: data.did,
        dids: data.dids,
        caller_id_mode: data.caller_id_mode,
        campaignStartTime: data.call_interval_time,
        nextCallTime: data.next_call_time,
        maxRetry: data.maxRetry,
        progressiveDialer: data.progressive_dialer,
        predictiveDialer: data.predictive_dialer,
        customer_ratio: data.customer_ratio,
        voice_boat_template: data.voice_boat_template,
        voice_bot_call_limit: data.voice_bot_call_limit,
        retry_type: data.retry_type,
        retry_interval_value: data.retry_interval_value,
        retry_time_gap: data.retry_time_gap,
        retry_schedule_data: data.retry_schedule_data,
      });

      if (templates.map(item => item.name).includes(props.data.campaign_name)) {
        setShowNameExist(true);
      }else{
        setShowNameExist(false);
      }

    } catch (err) {
      console.error("Failed to load template data:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to load template",
        text: "Please try again.",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setLoadingTemplate(false);
    }
  }, [auth, props]);

  const handleNext = () => {
    if (!props.cName?.trim()) {
      return Swal.fire({
        icon: "error",
        title: "Campaign Creation Failed",
        text: "Please provide a valid name for the campaign.",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
      });
    }
    props.setActive(props.next);
  };

  const isNextDisabled = props.agentDataLoading === "true" || loadingTemplate;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-5xl p-4 sm:p-6 mx-auto">
      <p className="text-sm text-slate-400">Step 1/7</p>
      <h1 className="text-lg font-bold">Let's Start with Campaign Details</h1>
      <p className="text-sm text-slate-400 border-b-2 pb-8">
        Please fill the details below, so that we can create new campaign
      </p>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 pt-8">
        {/* Campaign Name */}
        <div className="flex flex-col gap-1 w-full sm:w-auto sm:flex-1 sm:min-w-[200px] sm:max-w-[30rem]">
          <label htmlFor="campaign_name" className="text-sm text-slate-400">
            Campaign Name
          </label>
          <input
            id="campaign_name"
            type="text"
            value={props.cName}
            onChange={handleChange}
            placeholder="Campaign Name"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg h-[42px] w-full px-3 focus:ring-blue-500 focus:border-blue-500"
          />
          {showNameExist? 
            <small className="p-1 text-red-500">Campaign name cannot be the same as the template name.</small>
          : ""}
        </div>

        {/* Template picker */}
        <div className="flex flex-col gap-1 w-full sm:w-auto sm:flex-1 sm:min-w-[200px] sm:max-w-[30rem]">
          <label htmlFor="campaign_template" className="text-sm text-slate-400">
            Load from Template
          </label>
          <select
            id="campaign_template"
            value={selectedTemplate}          
            disabled={loadingTemplate}
            onChange={handleTemplate}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg h-[42px] w-full px-3 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
          >
            <option value="0">              
              {loadingTemplate ? "Loading…" : "Select template"}
            </option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Navigation */}
      <div className="pt-8 flex justify-between items-center">
        <button
          onClick={() => navigate("/admin/campaign/list")}
          className="px-5 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={isNextDisabled}
          className="text-sm sm:text-lg font-bold rounded-full w-28 sm:w-36 h-10 sm:h-12 text-white disabled:opacity-50 transition-opacity"
          style={{
            backgroundImage:
              "linear-gradient(177.88deg, #AFD462 1.79%, #6BC698 49.97%, #0FB1E2 82.06%)",
          }}
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

export default CampaignName;